import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
// import loadable from '@loadable/component';

import { TextLink } from '@vds/buttons';
import { Body, Title } from '@vds/typography';
import { TitleLockup } from '@vds/type-lockups';
import { Line } from '@vds/lines';
import { Tooltip } from '@vds/tooltips';

import { selectStateCache } from '../@selectors';
import { useCheckoutHooks } from '../@Redux/checkout.hooks';
import { Margin } from '../../../common/styles';
import {
  Container,
  DeviceAddOnContainer,
  DeviceContainer,
  PlanLinkContainer,
  Row,
  RowWrapper,
  TextLinkContainer,
  TilesContainer,
} from '../../../common/Cart/Tiles.styles';

import { renderHTML } from '../../../common/Helpers';
import EditButtonIcon from '../Components/EditButtonIcon';

import { checkoutLabels } from '../Components/labels';
import PriceInfo from '../Components/PriceInfo';
import { SetupLearnMoreLink } from '../../../Web/Cart/redesign/Components/setupLearnMoreLink';

import BroadbandInfoLink from '../../ImportantPlanInfo/BroadbandInfoLink';
import IPIModal from '../../ImportantPlanInfo/IPIModal';

const Plans = (props) => {
  const stateCache = useSelector((state) => selectStateCache(state), shallowEqual);
  const { plan, deviceItem, depletionType, SetUp, planPerks = [], eligible5GHomeBundle = [] } = useCheckoutHooks(stateCache);
  const [showPlanInfo, setShowPlanInfo] = useState(false);
  const { learnMoreHyperlinkText, learnMoreOverlayTitleText, learnMoreOverlayBodyText } = props;

  const imageUrl = deviceItem?.imageUrlMap?.largeImage || '';
  const deviceDescription = deviceItem?.description || '';
  const showLearnMoreLabel =
    !eligible5GHomeBundle?.length || (eligible5GHomeBundle?.length === 1 && eligible5GHomeBundle[0]?.bundleName?.includes('Self'));

  const toggleImportantPlanInfo = (value) => {
    if (!value) {
      setShowPlanInfo(value);
    }
  };

  // TODO: need to render common IPI component for this
  const getImportantInfo = () => (
    <Margin top="5px">
      <RowWrapper>
        <span style={{ marginRight: '6px' }}>
          <TextLink
            type="standAlone"
            role="button"
            data-testid="importantPlan_Link"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              setShowPlanInfo(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowPlanInfo(true);
              }
            }}
            data-track='{"type": "link","name": "important plan information","data":{"page":{"detail":"important plan information modal" }}}'
          >
            {checkoutLabels.ipiLinkText}
          </TextLink>
        </span>
        <span style={{ marginTop: '10px' }}>
          <BroadbandInfoLink />
        </span>
      </RowWrapper>
    </Margin>
  );

  const getDetailsContainer = () => {
    const { formattedPlanName, planPrice, strikeOffPrice } = plan;

    return (
      <div>
        <Margin bottom="4px">
          <Body primitive="p" size="small" color="#6F7171">
            Plan
          </Body>
        </Margin>
        <Row width="100%">
          <TitleLockup
            data={{
              title: {
                primitive: 'p',
                bold: false,
                size: 'titleMedium',
                children: renderHTML(formattedPlanName),
              },
              subtitle: {
                size: 'bodyLarge',
                children: <PriceInfo planPrice={planPrice} strikeOffPrice={strikeOffPrice} />,
              },
            }}
          />
          <PlanLinkContainer>
            <EditButtonIcon targetName="Plan" itemDesc={`Edit for ${formattedPlanName}`} />
          </PlanLinkContainer>
        </Row>
        {getImportantInfo()}
      </div>
    );
  };

  const getMiscellaneousContainer = () => {
    const miscList = [];
    if (deviceDescription) {
      miscList.push({ title: 'Included', desc: deviceDescription, isEdit: false, targetName: '', editDesc: 'Edit device' });
    }
    if (planPerks.length > 0) {
      const perksList = planPerks.map((item) => item.featureDesc);
      miscList.push({ title: 'Perks', desc: perksList.join(', '), isEdit: false, targetName: 'Plans', editDesc: 'Edit Perks' });
    }
    const isSetupOptions = eligible5GHomeBundle && eligible5GHomeBundle.length > 1;
    if (SetUp) {
      miscList.push({ title: 'SetUp', desc: SetUp, isEdit: isSetupOptions, targetName: 'Setup', editDesc: 'Edit Setup options' });
    }

    const result = miscList.map((item, index) => {
      const { title, desc, isEdit, targetName, editDesc } = item;
      const isSelfSetupInfo = title === 'SetUp' && !isSetupOptions && depletionType !== 'O';

      return (
        <DeviceAddOnContainer>
          <div>
            <Margin bottom="4px">
              <Body primitive="p" size="small" color="#6F7171">
                {title}
              </Body>
            </Margin>
            {desc && (
              <Row>
                <Body size="large" primitive="p">
                  {desc}
                </Body>

                <TextLinkContainer>
                  {isEdit && (
                    <Container width="20%">
                      <EditButtonIcon targetName={targetName} itemDesc={editDesc} />
                    </Container>
                  )}
                </TextLinkContainer>
                {isSelfSetupInfo && showLearnMoreLabel && (
                  <TextLinkContainer>
                    <Container>
                      <SetupLearnMoreLink {...{ learnMoreHyperlinkText, learnMoreOverlayTitleText, learnMoreOverlayBodyText }} />
                    </Container>
                  </TextLinkContainer>
                )}
              </Row>
            )}
          </div>
          {index < miscList.length - 1 && (
            <Margin top="22px" bottom="22px">
              <Line type="secondary" />
            </Margin>
          )}
        </DeviceAddOnContainer>
      );
    });

    return result;
  };

  return (
    <>
      <Margin top="10px">
        <Title size="medium" bold>
          {checkoutLabels.plansHeader}
        </Title>
      </Margin>

      <TilesContainer padding="24px" width="100%" top="12px" hasDevice>
        <DeviceContainer>
          <div className="detailsContainer">
            {imageUrl && (
              <div className="imageContent">
                <img src={imageUrl} style={{ width: '95px' }} alt="" />
              </div>
            )}
            <div className="rowContainer">{getDetailsContainer()}</div>
          </div>
          <div className="miscContainer">{getMiscellaneousContainer()}</div>
        </DeviceContainer>

        <div>
          <Margin top="10px">
            <Line type="secondary" />
          </Margin>
          <Margin top="10px" bottom="4px">
            <Body primitive="p" size="medium" color="#6F7171">
              {checkoutLabels.dawText}
              <Tooltip ariaLabel="tooltip - autopay" size="medium" surface="light">
                {checkoutLabels.dawTooltip}
              </Tooltip>
            </Body>
          </Margin>
        </div>
      </TilesContainer>
      {showPlanInfo && <IPIModal opened={showPlanInfo} onOpenedChange={(value) => toggleImportantPlanInfo(value)} />}
    </>
  );
};

export default Plans;
